import { useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext, useWatch } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import Button from "components/atoms/Button/Button";
import Dropdown from "components/atoms/Dropdown/Dropdown";
import testIds from "constants/testIds";
import { capitalizeFirstLetter } from "helpers/commonHelpers";
import { ReactComponent as PlusIcon } from "assets/images/icons/Plus.svg";
import { RequestFormActionType } from "pages/shipper/RequestForm/RequestFormState";
import {
  countPackingListRows,
  trackQuotePackingHandlingUnitAdded,
} from "pages/shipper/RequestForm/formEvents/requestFormAnalytics";
import HandlingUnitField from "./HandlingUnitField";
import ContainerTypeField from "./ContainerTypeField";
import LWHFields from "./LWHFields";
import SpecificationAdditionalField from "./SpecificationAdditionalField";
import UnitsField from "./UnitsField";
import WeightField from "./WeightField";
import CBMFields from "./CBMFields";
import NMFCField from "./NMFCField";
import CustomsInformation from "./CustomsInformation";
import AdditionalInstructions from "./AdditionalInstructions";
import { FormStepTypes } from "../../../../../../constants";
import PickupAndDeliverySelector from "./PickupAndDeliverySelector";
import {
  applyHandlingUnitFieldDefaults,
  clearDimensionFields,
  clearWeightField,
  PACKING_LIST_ADD_ITEM_SPECIAL_PROPERTIES,
  resetAddItemField,
} from "../packingListAddItemHelpers";

function PackingListAddItem({
  handlingUnits,
  isNMFCEnabled,
  NMFCValues,
  additionalInfoPlaceholder,
  append,
  selectedItemIndex,
  setSelectedItemIndex,
  setExpandedRow,
  expandedRow,
  watchRequestFormValues, // watch method from the actual form context
  getRequestFormValues, // getValues method from the actual form context
  setRequestFormValue, // setValue method from the actual form context
  currencyOptions,
  state,
  dispatch,
  isQuoteRequest = false,
}: any) {
  const intl = useIntl();
  const parentForm = useFormContext();
  const methods = useForm({ mode: "onChange" });
  const { getValues, setValue, handleSubmit, control, trigger } = methods;
  const packingListValues = useWatch({ control });
  const handlingUnitWatch = packingListValues?.handling_unit;
  const isEditingAnItem = selectedItemIndex !== null;
  const packingListWatch = parentForm.watch("packing_list");
  const stops = parentForm.watch("schedule.stops") || [];
  const hasMoreThanTwoStops = stops.length > 2;

  const [isHandlingUnitFieldVisible, setIsHandlingUnitFieldVisible] = useState(false);
  const [isContainerTypeFieldVisible, setIsContainerTypeFieldVisible] = useState(false);
  const [isLWHFieldsVisible, setIsLWHFieldsVisible] = useState(false);
  const [isSpecificationAdditionalFieldVisible, setIsSpecificationAdditionalFieldVisible] = useState(false);
  const [isWeightFieldVisible, setIsWeightFieldVisible] = useState(false);
  const [isCBMFieldsVisible, setIsCBMFieldsVisible] = useState(false);
  const [isUnitsFieldVisible, setIsUnitsFieldVisible] = useState(false);
  const [isNMFCFieldVisible, setIsNMFCFieldVisible] = useState(false);
  const [isCustomsFieldVisible, setIsCustomsFieldVisible] = useState(false);
  const [customsInformationEnabled, setCustomsInformationEnabled] = useState(() => (isEditingAnItem ? "no" : "manual"));
  const previousHandlingUnitRef = useRef<string | null | undefined>(undefined);

  const handlingUnitOptions = useMemo(
    () =>
      handlingUnits?.map((handlingUnit: any) => ({
        label: capitalizeFirstLetter(handlingUnit[Object.keys(handlingUnit)[0]].label),
        value: handlingUnit[Object.keys(handlingUnit)[0]].label,
      })),
    [handlingUnits],
  );

  const handlingUnitHashMap = useMemo(() => {
    if (handlingUnits && handlingUnits.length) {
      const data: any = {};
      handlingUnits.forEach((item: any) => {
        if (Object.keys(item)?.[0]) {
          data[Object.keys(item)?.[0].toLowerCase()] = item[Object.keys(item)?.[0]];
        }
      });
      return data;
    }
    return {};
  }, [handlingUnits]);

  const getHandlingUnitSettings = () => handlingUnitHashMap[getValues(`handling_unit`)?.toLowerCase?.()];

  const selectedHandlingUnitSettings = useMemo(
    () => handlingUnitHashMap[handlingUnitWatch?.toLowerCase?.()],
    [handlingUnitHashMap, handlingUnitWatch],
  );

  useEffect(() => {
    if (isEditingAnItem) return;
    let unsavedChanges = false;
    if (
      packingListValues?.height ||
      packingListValues?.length ||
      packingListValues?.units ||
      packingListValues?.weight_piece ||
      packingListValues?.width ||
      packingListValues?.nmfc_class ||
      packingListValues?.additional_field ||
      !packingListWatch?.length
    ) {
      unsavedChanges = true;
    }
    dispatch({
      type: RequestFormActionType.UPDATE_UNSAVED_CHANGES,
      payload: { key: FormStepTypes.PACKING_LIST, value: unsavedChanges },
    });
  }, [packingListValues, isEditingAnItem, packingListWatch]);

  // If user has selected an item, populate the form with the selected item's values
  useEffect(() => {
    if (selectedItemIndex !== null) {
      const selectedItem = getRequestFormValues(`packing_list.${selectedItemIndex}`);
      if (selectedItem) {
        Object.keys(selectedItem ?? {}).forEach((key) => {
          const value = selectedItem?.[key];
          if (value) setValue(`${key}`, value);
        });
        // Set the customs information mode based on the selected item's customs information
        if (selectedItem.good_customs_information?.length > 0) {
          if (selectedItem.good_customs_information[0]?.attachment?.length > 0) setCustomsInformationEnabled("file");
          else setCustomsInformationEnabled("manual");
        }
      }
    }
  }, [selectedItemIndex]);

  // Show/hide fields based on the selected handling unit
  useEffect(() => {
    // If the user has not selected a handling unit, set the default handling unit
    if (!getValues(`handling_unit`)) {
      setValue("handling_unit", handlingUnitOptions[0]?.value);
    }

    setIsHandlingUnitFieldVisible(handlingUnits?.length > 0 && selectedItemIndex == null);
    setIsContainerTypeFieldVisible(getValues(`handling_unit`) === "container");
    setIsLWHFieldsVisible(getValues(`handling_unit`) !== "container" && getHandlingUnitSettings()?.l_w_h !== "off");
    setIsWeightFieldVisible(getHandlingUnitSettings()?.weight !== "off");
    setIsCBMFieldsVisible(getHandlingUnitSettings()?.l_w_h_cbm);
    setIsUnitsFieldVisible(getHandlingUnitSettings()?.quantity !== "off");
    setIsNMFCFieldVisible(getValues("handling_unit") && getNMFCSetting()?.toLowerCase?.() !== "off");
    setIsSpecificationAdditionalFieldVisible(
      (getHandlingUnitSettings()?.additional_field ?? "off") !== "off",
    );
    setIsCustomsFieldVisible(getHandlingUnitSettings()?.customs_value);
  }, [handlingUnitWatch]);

  useEffect(() => {
    if (isEditingAnItem || !handlingUnitWatch) return;

    const previousHandlingUnit = previousHandlingUnitRef.current;
    if (previousHandlingUnit != null && previousHandlingUnit !== handlingUnitWatch) {
      clearDimensionFields(setValue);
      clearWeightField(setValue);
    }

    applyHandlingUnitFieldDefaults({
      setValue,
      getRequestFormValues,
      getValues,
      handlingUnit: handlingUnitWatch,
      handlingUnitSettings: selectedHandlingUnitSettings,
    });

    previousHandlingUnitRef.current = handlingUnitWatch;
  }, [
    getRequestFormValues,
    getValues,
    handlingUnitWatch,
    isEditingAnItem,
    selectedHandlingUnitSettings,
    setValue,
  ]);

  // Keep nested Add Item units in sync with the global PL unit dropdowns.
  // Without this, changing Inch/lbs after the form seeded profile defaults (e.g. cm/kg)
  // left stale nested m_unit/w_unit that overrode the global selection on first Add.
  const globalMUnit = useWatch({
    control: parentForm.control,
    name: "packing.global_configuration.m_unit",
  });
  const globalWUnit = useWatch({
    control: parentForm.control,
    name: "packing.global_configuration.w_unit",
  });

  useEffect(() => {
    if (!handlingUnitWatch || isEditingAnItem) return;
    if (globalMUnit) setValue("m_unit", globalMUnit);
  }, [globalMUnit, handlingUnitWatch, isEditingAnItem, setValue]);

  useEffect(() => {
    if (!handlingUnitWatch || isEditingAnItem) return;
    if (globalWUnit) setValue("w_unit", globalWUnit);
  }, [globalWUnit, handlingUnitWatch, isEditingAnItem, setValue]);

  /**
   * Reusable validation for this nested react-hook-form instance. This block must not use a
   * native form element because PackingListAddItem renders inside RequestForm's outer form.
   * @param callback The callback to execute if the form is valid
   */
  const performValidation = async (callback: () => void) => {
    const isValid = await trigger(); // Trigger the form validation manually
    if (isValid) {
      callback(); // If the form is valid, execute the provided callback
      parentForm.clearErrors(`packing_list.${selectedItemIndex}`);
    }
  };

  const updateSpecialPropertiesValue = (itemToAdd: any) => {
    Object.keys(itemToAdd).forEach((key) => {
      if (PACKING_LIST_ADD_ITEM_SPECIAL_PROPERTIES.includes(key)) {
        if (
          itemToAdd[key] &&
          !!getRequestFormValues("packing_list")?.length &&
          !getRequestFormValues("packing_list").every((item: any) => item[key])
        ) {
          itemToAdd[key] = false;
        }
      }
    });
  };

  // Add the temp item to the packing list and clear the form
  const onAddItem = () => {
    performValidation(() => {
      // Global unit selection wins over nested form copies so the dropdown choice is persisted
      const globalConfig = getRequestFormValues("packing.global_configuration") || {};
      const tempItem = {
        ...globalConfig,
        ...getValues(),
        m_unit: globalConfig.m_unit || getValues("m_unit"),
        w_unit: globalConfig.w_unit || getValues("w_unit"),
      };
      updateSpecialPropertiesValue(tempItem);

      // If the user has entered customs information, update the packing list currencies
      if (tempItem?.good_customs_information?.length > 0) {
        updatePackingListCurrencies(tempItem.good_customs_information[0]?.currency_code);
      }

      // Append the temp item to the packing list
      append(tempItem);

      if (isQuoteRequest && (tempItem.handling_unit || "").toLowerCase() !== "container") {
        const nextPackingList = [...(getRequestFormValues("packing_list") || []), tempItem];
        trackQuotePackingHandlingUnitAdded(countPackingListRows(nextPackingList).total_handling_units);
      }

      // Clear form (including handling unit); default handling unit is re-applied via useEffect
      Object.keys(tempItem).forEach((key) => resetAddItemField(setValue, key));
      setCustomsInformationEnabled("no");
      previousHandlingUnitRef.current = undefined;
    });
  };

  // Save the changes to the selected item and clear the form
  const onSaveItem = () => {
    performValidation(() => {
      const tempItem = getValues();
      const prevHandlingUnit = getValues("handling_unit");

      // If the user has entered customs information, update the packing list currencies
      if (tempItem?.good_customs_information?.length > 0) {
        updatePackingListCurrencies(tempItem.good_customs_information[0]?.currency_code);
      }

      // If the user has not entered customs information, set the value to null to clear any previous values
      if (!tempItem.good_customs_information?.length) tempItem.good_customs_information = null;

      // If the user removed additional information, set the value to null to clear any previous values
      if (!tempItem.additional_information) tempItem.additional_information = null;

      // Loop through the temp item and set the values to the selected item index
      Object.keys(tempItem).forEach((key) => {
        setRequestFormValue(`packing_list.${selectedItemIndex}.${key}`, tempItem[key]);
        resetAddItemField(setValue, key);
      });
      setCustomsInformationEnabled("no");
      setValue("handling_unit", prevHandlingUnit); // Set the handling unit back to the previous value, improves UX
      clearSelectedItemAndCollapseRows();
    });
  };

  // Cancel editing an item and clear the form
  const onCancel = () => {
    const prevHandlingUnit = getValues("handling_unit");
    Object.keys(getValues()).forEach((key) => resetAddItemField(setValue, key));
    setValue("handling_unit", prevHandlingUnit); // Set the handling unit back to the previous value, improves UX

    clearSelectedItemAndCollapseRows();
  };

  // Clear the selected item and collapse the table rows
  const clearSelectedItemAndCollapseRows = () => {
    setSelectedItemIndex(null);
    expandedRow.toggleRowExpanded();
    setExpandedRow(null);
  };

  // Get the container options for the selected handling unit
  const getContainerOptions = () => {
    if (handlingUnits) {
      const container = handlingUnits.find((item: any) => item.container);
      const containerOptions = container?.container?.handling_unit_options.map((option: any) => ({
        label: option.value,
        is_active: false,
        value: option.key,
      }));
      return containerOptions;
    }
  };

  // Get the NMFC class for the selected handling unit
  const getNMFCSetting = () =>
    getHandlingUnitSettings()?.nmfc === "Location settings" ? isNMFCEnabled : getHandlingUnitSettings()?.nmfc;

  // Update the packing list currencies to the selected currency
  const updatePackingListCurrencies = (selectedCurrency: string) => {
    const packingListItems = getRequestFormValues("packing_list");
    packingListItems.forEach((item: any, index: number) => {
      if (
        item?.good_customs_information?.length > 0 &&
        item.good_customs_information[0]?.currency_code !== selectedCurrency
      ) {
        setRequestFormValue(`packing_list.${index}.good_customs_information.0.currency_code`, selectedCurrency);
      }
    });
  };

  // Handle the customs information eabled dropdown change
  const onCustomsInformationEnabledChange = (o: any) => {
    // First, clear the customs information values this is because each mode has different fields
    setValue("good_customs_information", []);
    setCustomsInformationEnabled(o.value);
  };

  const submitPackingListItem = handleSubmit(isEditingAnItem ? onSaveItem : onAddItem);

  const onPackingListSectionKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Enter") return;
    const target = e.target as HTMLElement;
    if (target.tagName === "TEXTAREA" || target.isContentEditable) return;
    if (target.tagName !== "INPUT" && target.tagName !== "SELECT") return;
    e.preventDefault();
    e.stopPropagation();
    submitPackingListItem().catch(() => {});
  };

  return (
    <FormProvider {...methods}>
      <div onKeyDown={onPackingListSectionKeyDown}>
        <div className={isEditingAnItem ? "" : "packing-list-add-item-container"}>
          <div className="d-flex flex-wrap align-items-end" style={{ gap: 5 }}>
            {isHandlingUnitFieldVisible && <HandlingUnitField options={handlingUnitOptions} />}
            {isContainerTypeFieldVisible && <ContainerTypeField options={getContainerOptions()} />}
            {isSpecificationAdditionalFieldVisible && (
              <SpecificationAdditionalField
                placeholder={
                  getHandlingUnitSettings()?.additional_field_label ??
                  intl.formatMessage({
                    id: "new-request.packing-list.additionalField",
                    defaultMessage: "Additional Field",
                  })
                }
                isRequired={getHandlingUnitSettings()?.additional_field === "mandatory"}
              />
            )}
            {isLWHFieldsVisible && <LWHFields isRequired={getHandlingUnitSettings()?.l_w_h === "mandatory"} />}
            {isUnitsFieldVisible && <UnitsField isRequired={getHandlingUnitSettings()?.quantity === "mandatory"} />}
            {isWeightFieldVisible && (
              <WeightField
                isRequired={getHandlingUnitSettings()?.weight === "mandatory"}
                getRequestFormValues={getRequestFormValues}
              />
            )}
            {isCBMFieldsVisible && <CBMFields />}
            {isNMFCFieldVisible && (
              <NMFCField NMFCValues={NMFCValues} isRequired={getNMFCSetting()?.toLowerCase?.() === "mandatory"} />
            )}
            {isCustomsFieldVisible && (
              <div className="packing-list-add-item-input-large">
                <label>
                  <FormattedMessage id="new-request.packing-list.customsInfo" defaultMessage="Customs info" />
                </label>
                <Dropdown
                  className="schedule-dropdown"
                  value={customsInformationEnabled}
                  onChange={(o: any) => onCustomsInformationEnabledChange(o)}
                  options={[
                    { label: "Yes", value: "manual" },
                    { label: "Yes, via file", value: "file" },
                    { label: "No", value: "no" },
                  ]}
                />
              </div>
            )}
            {/* Add, Save, Cancel buttons */}
            <div style={{ marginTop: 23 }}>
              {!isEditingAnItem && (
                <div data-testid={testIds.REQUEST_FORM_PACKING_LIST_ADD_ITEM_BUTTON}>
                  <Button
                    variant="primary"
                    className="pl-2 pb-0 pt-0"
                    label={intl.formatMessage({ id: "general.add", defaultMessage: "Add" })}
                    onClick={onAddItem}
                    data-testid="add-item-button"
                  >
                    <PlusIcon />
                  </Button>
                </div>
              )}
              {isEditingAnItem && (
                <div className="d-flex">
                  <Button
                    variant="primary"
                    label={intl.formatMessage({ id: "general.save", defaultMessage: "Save" })}
                    onClick={onSaveItem}
                  />
                  <Button
                    variant="outline-primary"
                    label={intl.formatMessage({ id: "general.cancel", defaultMessage: "Cancel" })}
                    onClick={onCancel}
                    className="ml-2"
                  />
                </div>
              )}
            </div>
          </div>
          {hasMoreThanTwoStops && <PickupAndDeliverySelector watchRequestFormValues={watchRequestFormValues} />}
          {isCustomsFieldVisible && (customsInformationEnabled === "manual" || customsInformationEnabled === "file") && (
            <CustomsInformation
              getRequestFormValues={getRequestFormValues}
              currencyOptions={currencyOptions}
              state={state}
              mode={customsInformationEnabled}
              selectedItemIndex={selectedItemIndex}
              isQuoteRequest={isQuoteRequest}
            />
          )}
          <AdditionalInstructions placeholder={additionalInfoPlaceholder} />
        </div>
      </div>
    </FormProvider>
  );
}

export default PackingListAddItem;
